/* eslint-disable max-lines */
import { IconNames } from '@blueprintjs/icons';
import { BenefitTypeDisplayTextMap } from '@strata/constants';
import { formatDate, getProductName, sortAsc } from '@strata/helper-functions';
import { ToggleButton } from '@strata/react-buttons';
import { GridWithSearch, ProductCell, SecondaryData } from '@strata/react-grid';
import { SuggestSingleSelect } from '@strata/react-selects';
import { ServicingRate } from '@strata/react-servicing-rate';
import { Icon } from '@strata/react-status-icons';
import dayjs from 'dayjs';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import { ListPageHeader } from 'components/ListPageHeader';
import { BackstopPharmacyStatusIndicator } from 'pages/routeManagement/backstopPharmacies/BackstopPharmacyStatusIndicator';
import { DeleteBackstopPharmacyDialog } from 'pages/routeManagement/backstopPharmacies/dialogs/DeleteBackstopPharmacyDialog';
import { RankedPharmaciesCell } from 'pages/routeManagement/backstopPharmacies/RankedPharmacies';
import { CREATE_ROUTE, GLOBAL_SEGMENT_ID } from 'services/constants';
import { handleError } from 'services/ErrorManagement';
import { get, useGet } from 'services/Http';

const getBackstopPharmacyStatus = (backstopPharmacy) => {
    if (
        !!backstopPharmacy.backstop_end_date &&
        dayjs().isAfter(backstopPharmacy.backstop_end_date, 'day')
    ) {
        return BackstopPharmacyStatusIndicator.Statuses.INACTIVE;
    }
    if (dayjs().isBefore(backstopPharmacy.backstop_start_date, 'day')) {
        return BackstopPharmacyStatusIndicator.Statuses.PEND_START;
    }
    return BackstopPharmacyStatusIndicator.Statuses.ACTIVE;
};

const getBackstopPharmacies = (segmentId) =>
    get(
        segmentId === GLOBAL_SEGMENT_ID
            ? '/assignment/backstop_pharmacies/global'
            : `/assignment/backstop_pharmacies/segment/${segmentId}`,
    );

const CustomSearchValueHandlers = {
    // Search reaches every pharmacy of a universal assignment, not only the first
    pharmacies: (pharmacies) => pharmacies?.map(({ supplier }) => supplier).join(','),
};

const columns = ({ setBackstopPharmacyToDelete, navigate }) => [
    {
        header: 'Product',
        accessorKey: 'product',
        size: 200,
        cell: ({ getValue, row }) => (
            <span className="flex__gap--small" style={{ alignItems: 'center' }}>
                {row.original.segment_id === null && (
                    <Icon icon={IconNames.GLOBE_NETWORK} title="Universal Assignment" />
                )}
                <span style={{ flexGrow: 1, overflow: 'hidden' }}>
                    <ProductCell product={getValue()} isBrand={row.original.is_brand} primary />
                </span>
            </span>
        ),
    },
    {
        header: 'Benefit',
        accessorKey: 'benefit',
        size: 80,
    },
    {
        header: 'Place Of Service',
        accessorKey: 'placeOfService',
        size: 120,
    },
    {
        header: 'Pharmacy',
        id: 'supplier',
        // Universal rows sort on their rank 1 pharmacy
        accessorFn: (row) => row.pharmacies[0]?.supplier,
        size: 180,
        cell: ({ getValue, row }) =>
            row.original.segment_id === null ? (
                <RankedPharmaciesCell
                    pharmacies={row.original.pharmacies}
                    referentPriceType={row.original.referent_price_type}
                    content={RankedPharmaciesCell.Content.names}
                />
            ) : (
                <SecondaryData data={getValue()} />
            ),
    },
    {
        header: 'Contract Rate',
        id: 'backstop_rate',
        // Universal rows sort on their rank 1 rate
        accessorFn: (row) => row.pharmacies[0]?.backstop_rate,
        size: 110,
        cell: ({ getValue, row }) =>
            row.original.segment_id === null ? (
                <RankedPharmaciesCell
                    pharmacies={row.original.pharmacies}
                    referentPriceType={row.original.referent_price_type}
                    content={RankedPharmaciesCell.Content.rates}
                />
            ) : (
                <SecondaryData
                    data={
                        <ServicingRate
                            servicingRate={getValue()}
                            referentPriceType={row.original.referent_price_type}
                        />
                    }
                />
            ),
    },
    {
        header: 'Reason',
        accessorKey: 'backstop_reason',
        size: 100,
    },
    {
        header: 'Start Date',
        accessorKey: 'backstop_start_date',
        size: 100,
        cell: ({ getValue }) => <SecondaryData data={formatDate(getValue())} />,
    },
    {
        header: 'End Date',
        accessorKey: 'backstop_end_date',
        size: 100,
        cell: ({ getValue }) => <SecondaryData data={formatDate(getValue())} />,
    },
    {
        header: 'Status',
        accessorKey: 'backstopStatus',
        size: 100,
        cell: ({ getValue }) => <BackstopPharmacyStatusIndicator status={getValue()} />,
    },
    {
        header: 'Actions',
        accessorKey: 'backstop_pharmacy_id',
        size: 70,
        enableSorting: false,
        cell: ({ getValue, row }) => (
            <div className="sds_filter-list__actions">
                <Icon
                    icon={IconNames.EDIT}
                    intent={Icon.Intent.SECONDARY}
                    title="Edit"
                    onClick={() =>
                        navigate(
                            `/route-management/post-live-referral-assignment/${row.original.segment_id ?? GLOBAL_SEGMENT_ID}/${getValue()}`,
                        )
                    }
                />
                {!row.original.is_used && (
                    <Icon
                        icon={IconNames.TRASH}
                        intent={Icon.Intent.SECONDARY}
                        title="Delete"
                        onClick={() => setBackstopPharmacyToDelete(row.original)}
                    />
                )}
            </div>
        ),
    },
];
export const BackstopPharmacies = () => {
    const { segmentId } = useParams();
    const navigate = useNavigate();
    const { plrdrGlobalRouting } = useFlags();

    const segments = useGet('/reference/payer_segments');
    const segmentOptions = segments && [
        ...(plrdrGlobalRouting
            ? [
                  {
                      name: 'All (universal)',
                      value: GLOBAL_SEGMENT_ID,
                      payerId: GLOBAL_SEGMENT_ID,
                  },
              ]
            : []),
        ...segments
            .map((segment) => ({
                name: segment.segment_name,
                secondaryData: segment.segment_id,
                value: segment.segment_id,
                payerId: segment.payer_id,
            }))
            .sort((a, b) => sortAsc(a.name, b.name)),
    ];

    const [backstopPharmacies, setBackstopPharmacies] = useState();
    useEffect(() => {
        void (async () => {
            try {
                if (segmentId) {
                    setBackstopPharmacies(await getBackstopPharmacies(segmentId));
                }
            } catch (err) {
                handleError('Error fetching Post Live Referral Assignments.', err);
            }
        })();
        return () => {
            setBackstopPharmacies();
        };
    }, [segmentId]);

    const suppliers = useGet('/reference/suppliers/parents');
    const productServices = useGet('/reference/product_benefit_configs/');
    const productsMap = Object.fromEntries(
        (productServices ?? []).map((product) => [
            product.oid,
            {
                description: product.description,
                brandDescription: product.brand_product_description,
                genericDescription: product.generic_product_description,
            },
        ]),
    );

    const placesOfService = useGet('/reference/product_benefit_configs/places');
    const placeOfServiceMap = Object.fromEntries(
        (placesOfService ?? []).map(({ code, description }) => [code, description]),
    );

    const [backstopPharmacyToDelete, setBackstopPharmacyToDelete] = useState();
    const [hideInactiveAssignments, setHideInactiveAssignments] = useState(true);
    const isUniversal = segmentId === GLOBAL_SEGMENT_ID;

    const mappedBackstopPharmacies = backstopPharmacies
        ?.map((backstopPharmacy) => ({
            ...backstopPharmacy,
            benefit: BenefitTypeDisplayTextMap[backstopPharmacy.benefit],
            product: getProductName(
                productsMap,
                backstopPharmacy.product_service_oid,
                backstopPharmacy.is_brand,
            ),
            placeOfService:
                placeOfServiceMap[backstopPharmacy.place_of_service] ?? 'All Places of Service',
            // A segment config has a single pharmacy; a universal config ranks one or more
            pharmacies: [...(backstopPharmacy.pharmacies ?? [])]
                .sort((a, b) => a.rank - b.rank)
                .map((pharmacy) => ({
                    ...pharmacy,
                    supplier: suppliers?.find(
                        (supplier) => supplier.supplier_npi === pharmacy.supplier_npi,
                    )?.organization_name,
                })),
            backstopStatus: getBackstopPharmacyStatus(backstopPharmacy),
        }))
        .filter(
            ({ backstopStatus }) =>
                !hideInactiveAssignments ||
                backstopStatus !== BackstopPharmacyStatusIndicator.Statuses.INACTIVE,
        );

    return (
        <div className="sds_list-page">
            <div className="sds_list-page__content--top">
                <SuggestSingleSelect
                    id="segmentId"
                    name="segmentId"
                    label="Payer Segment"
                    required
                    isLoading={!segmentOptions}
                    values={segmentOptions}
                    onChange={(newValue) =>
                        navigate(
                            `/route-management/post-live-referral-assignment/${newValue?.value ?? ''}`,
                        )
                    }
                    selectedItem={segmentOptions?.find((segment) => segment.value === segmentId)}
                />
            </div>
            {segmentId && (
                <div className="sds_main-content__container">
                    <DeleteBackstopPharmacyDialog
                        backstopPharmacyToDelete={backstopPharmacyToDelete}
                        onClose={async () => {
                            try {
                                setBackstopPharmacyToDelete();
                                setBackstopPharmacies(await getBackstopPharmacies(segmentId));
                            } catch (err) {
                                handleError('Error fetching Post Live Referral Assignments.', err);
                            }
                        }}
                    />
                    <ListPageHeader
                        pageTitle={
                            isUniversal ? 'Universal Assignments' : 'Post Live Referral Assignment'
                        }
                        rightContent={
                            <ToggleButton
                                label="Hide Inactive Assignments"
                                isOn={hideInactiveAssignments}
                                onChange={(e) =>
                                    setHideInactiveAssignments(e.currentTarget.checked)
                                }
                            />
                        }
                        buttons={[
                            {
                                label: isUniversal
                                    ? 'Create Universal Assignment'
                                    : 'Create Assignment',
                                path: `/route-management/post-live-referral-assignment/${segmentId}/${CREATE_ROUTE}`,
                            },
                            {
                                label: isUniversal
                                    ? 'Bulk Upload Universal Assignments'
                                    : 'Bulk Upload Assignments',
                                path: '/route-management/post-live-referral-assignment/bulk-upload',
                            },
                        ]}
                    />
                    <div className="sds_main-content__grid">
                        <GridWithSearch
                            data={mappedBackstopPharmacies}
                            isLoading={!mappedBackstopPharmacies}
                            sortProperties={[{ id: 'product', desc: false }]}
                            customSearchValueHandlers={CustomSearchValueHandlers}
                            columns={columns({ setBackstopPharmacyToDelete, navigate })}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};
